import * as THREE from 'three';

/**
 * Wave P-3 (PLAN-CITY3 §3): a cheap standing-water material for the sewer
 * den's `water_basin` node (today: literally the concrete material with a
 * different palette texel, per CITY3-FINDINGS.md — reads as stone, not
 * water). No image asset, no `three/examples/jsm/objects/Water` (a second
 * scene render per frame, well past this project's mobile perf budget —
 * PLAN-CITY3 §3's own law). Instead: a small procedural 128x128 normal map
 * (two overlaid sine ripples baked in, per the plan's own spec) plus
 * roughness/transparency on a plain MeshStandardMaterial, animated by
 * scrolling the normal map's UV offset at two independent speeds via the
 * mesh's own `onBeforeRender` hook — fires automatically on every render
 * call, so this needs no per-frame call from main.ts's game-tick loop
 * (Wave P doesn't own main.ts; see PLAN-CITY3 §13's concurrency map).
 * `applyWaterMaterial(mesh)` is intentionally reusable as-is for the park
 * pool (Wave N) — same call, any mesh.
 */

function buildRippleNormalMap(): THREE.DataTexture {
  const SIZE = 128;
  const data = new Uint8Array(SIZE * SIZE * 4);

  // Two independent sine ripples — different frequency, direction, and
  // amplitude — summed into one height field, matching the plan's own
  // "two-sine normal map" spec. u/v are normalized [0,1) tile coordinates.
  const heightAt = (u: number, v: number): number => {
    const a = Math.sin((u * 9 + v * 3) * Math.PI * 2) * 0.6;
    const b = Math.sin((u * -4 + v * 11) * Math.PI * 2) * 0.4;
    return a + b;
  };

  const STEP = 1 / SIZE;
  for (let y = 0; y < SIZE; y++) {
    const v = y / SIZE;
    for (let x = 0; x < SIZE; x++) {
      const u = x / SIZE;
      // Finite-difference slope -> a perturbed tangent-space normal, packed
      // into RGB the standard way ((n*0.5+0.5)*255 per channel).
      const h = heightAt(u, v);
      const hx = heightAt(u + STEP, v);
      const hy = heightAt(u, v + STEP);
      const dx = (hx - h) * 0.35;
      const dy = (hy - h) * 0.35;
      const n = new THREE.Vector3(-dx, -dy, 1).normalize();
      const i = (y * SIZE + x) * 4;
      data[i] = Math.round((n.x * 0.5 + 0.5) * 255);
      data[i + 1] = Math.round((n.y * 0.5 + 0.5) * 255);
      data[i + 2] = Math.round((n.z * 0.5 + 0.5) * 255);
      data[i + 3] = 255;
    }
  }

  const tex = new THREE.DataTexture(data, SIZE, SIZE, THREE.RGBAFormat);
  tex.colorSpace = THREE.NoColorSpace; // a normal map is data, not a color to sRGB-decode
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(3, 3);
  tex.needsUpdate = true;
  return tex;
}

/** Swaps `mesh`'s material for the procedural water look and wires its own scroll animation — self-contained, no caller-side per-frame call needed. */
export function applyWaterMaterial(mesh: THREE.Mesh): void {
  const normalMap = buildRippleNormalMap();
  const mat = new THREE.MeshStandardMaterial({
    color: 0x1c3236,
    roughness: 0.12,
    metalness: 0.05,
    transparent: true,
    opacity: 0.82,
    normalMap,
    normalScale: new THREE.Vector2(0.7, 0.7),
  });
  mesh.material = mat;
  // Two independent scroll rates (X vs Y) so the baked-in ripple pattern's
  // own motion never reads as a single uniform slide — driven from the
  // mesh's own onBeforeRender, which the renderer calls automatically every
  // frame this mesh is drawn, regardless of what main.ts's game-tick loop
  // does (a purely cosmetic effect, matching how this project already
  // treats sky/ambient decoration as wall-clock-driven, not sim-time-driven).
  mesh.onBeforeRender = () => {
    const t = performance.now() / 1000;
    normalMap.offset.set((t * 0.015) % 1, (t * 0.024) % 1);
  };
}
